<template>
  <div v-rect-loading="loading" class="consent-page">
    <div class="head">
      <span class="label">{{ $t(`${i18nPrefix}.label.myConsent`) }}</span>
    </div>
    <div class="privacy-policy">
      <Agreement
        v-if="showStatement"
        isConsent
        ref="agreement"
        :class="[!companyId && 'no-policy']"
        :policyInfo="policyInfo"
        :showSystemPolicy="true"
        :isAgreeSystemPolicy="isAgreeSystemPolicy"
      >
        <template #btnGroup>
          <el-button class="cancel-btn footer-button" @click="goHomepage">{{ cancelBtn }}</el-button>
          <el-popover
            popper-class="custom-consent-confirm-popover"
            trigger="manual"
            v-model="showPopConfirm"
          >
            <div class="title">{{ confirmTip }}</div>
            <div class="btn">
              <el-button type="text" @click="handleCancelPop" size="mini">{{ $t('common.btn.cancel') }}</el-button>
              <el-button type="primary" @click="handleConfirmPop" size="mini">{{ $t('common.btn.confirm') }}</el-button>
            </div>
            <template #reference>
              <el-button class="confirm-btn footer-button" @click="handleSubmit">{{ submitBtn }}</el-button>
            </template>
          </el-popover>
        </template>
      </Agreement>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import Agreement from '@/components/agreement/agreementContent.vue'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'

export default {
  components: {
    Agreement
  },
  data () {
    return {
      loading: false,
      i18nPrefix: 'personal.home',
      showStatement: false,
      jobCompanyId: null,
      isAgreeSystemPolicy: false,
      policyInfo: {},
      showPopConfirm: false,
      confirmTip: null
    }
  },
  computed: {
    companyId () {
      return this.$route.query.company_id
    },
    backUrl () {
      return this.$route.query.back_url
    },
    cancelBtn () {
      return this.$t('common.btn.back')
    },
    submitBtn () {
      return this.$t('common.btn.save')
    }
  },
  created () {
    this.initPageData()
  },
  methods: {
    ...mapActions('personal', ['getConsentAgreementStatus', 'updateConsentAgreementStatus']),
    async initPageData () {
      this.showStatement = false
      this.loading = true
      try {
        const resp = await this.getConsentAgreementStatus({
          company_id: this.companyId || null
        })
        if (resp) {
          this.jobCompanyId = resp.company_id
          this.isAgreeSystemPolicy = resp.is_agreed_system_policy
          this.policyInfo = resp.policy_info || {}
          addI18nProperty(this.policyInfo, 'companyName', 'company_cn_name', 'company_en_name')
          if (resp.is_company_policy_update) {
            this.$message.warning(this.$t('admin.company.agreement.message.consentUpdated'))
          }
        }
      } finally {
        this.loading = false
        this.showStatement = true
      }
    },
    async handleSubmit () {
      const agreementRef = this.$refs.agreement
      if (!agreementRef.agreeSystemPolicy) {
        // 系统级没同意，则提示注销信息
        this.showPopConfirm = true
        this.confirmTip = this.$t('common.message.logoff')
        return
      }
      _.forEach(agreementRef.bodyContent, (policy) => {
        if (!policy.is_required) {
          return
        }
        const agree = agreementRef.formModel['policy_' + policy.id]
        if (!agree) {
          // 未同意公司required协议，提示revoke all
          this.showPopConfirm = true
          this.confirmTip = this.$t('common.message.revokeAllApplication')
          return false
        }
      })
      if (!this.showPopConfirm) {
        this.updateConsentInfo()
      }
    },
    handleCancelPop () {
      this.showPopConfirm = false
    },
    handleConfirmPop () {
      this.updateConsentInfo()
      this.showPopConfirm = false
    },
    goHomepage () {
      this.$eventBus.$emit('changeHomePageInfo')
      this.$router.push({ path: '/recruiting/personal/home', query: this.$route.query })
    },
    async updateConsentInfo () {
      const agreementRef = this.$refs.agreement
      this.loading = true
      try {
        const policyItemIds = []
        for (const policy in agreementRef.formModel) {
          if (agreementRef.formModel[policy]) {
            policyItemIds.push(policy.substring(7))
          }
        }
        const agreementInfo = {
          company_id: this.companyId,
          is_agreed_system_policy: agreementRef.agreeSystemPolicy,
          agree_item_ids: policyItemIds
        }

        await this.updateConsentAgreementStatus(agreementInfo)
        if (!agreementRef.agreeSystemPolicy) {
          this.$router.push('/passport/recruiter/login')
        } else if (this.backUrl) {
          window.location.href = this.backUrl
        } else {
          this.goHomepage()
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.consent-page {
  min-height: calc(100vh - var(--header-height, 124px) - 28px);
  min-height: calc(100dvh - var(--header-height, 124px) - 28px);
  display: flex;
  flex-direction: column;

  .head {
    display: flex;
    position: sticky;
    top: -14px;
    height: 52px;
    padding: 16px 0 16px 40px;
    z-index: 3;
    background-color: #fff;
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.08);

    .label {
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      color: #606266;
    }
  }

  .privacy-policy {
    flex: 1;
    background: #fff;

    ::v-deep {
      .resume-applicant-pc {
        max-height: initial;
        top: 0;
        border: none;

        .company-policy-popup-content-container {
          &::before {
            display: none;
          }
        }
      }
    }

    .no-policy {
      ::v-deep {
        .company-policy-popup-content-container {
          .container-body {
            min-height: 40px;
          }
        }
      }
    }

    .footer-button {
      font-size: 14px;
      border: 0;
      cursor: pointer;
      line-height: 14px;
      padding: 10px 30px;
      background: var(--custom-color, #0096cc);
      color: #fff;

      &:first-child {
        margin-left: 0;
        background: #a9a9a9;
        color: #fff;
        border: 1px solid #a9a9a9;
      }

      &:last-child {
        background: var(--custom-color, #0096cc);
        color: #fff;
        border: 1px solid var(--custom-color, #0096cc);
      }
    }

    .cancel-btn {
      font-size: 14px;
      border: 0;
      cursor: pointer;
      line-height: 14px;
      padding: 10px 30px;
      background: #a9a9a9;
      color: #fff;
      border: 1px solid #a9a9a9;
    }
  }
}

.custom-consent-confirm-popover {
  .title {
    width: 100%;
    margin: 12px 0;
    word-break: break-word;
  }

  .btn {
    text-align: right;
  }
}
</style>
